import { get } from 'lodash';

import {
  APPLICANT_QUESTIONS,
  ENTITY_TYPE,
  FIELD_PATHS,
  URL_PARAM_APPLICANT_ID,
  URL_PARAM_ASSET_ID,
} from '@funnels/constants';
import {
  LiabilityContractsMap,
  OptionalTargetParams,
  PostProductSelectContract,
  Question,
  QuestionConfig,
  StoreApplicant,
  TargetReturn,
} from '@funnels/types';
import {
  AccountantLayout,
  AccountDetails,
  ApplicantLayout,
  ConveyancerLayout,
  LicnesedBuilderLayout,
  PropertyLayout,
} from '@funnels/ui-layouts/customer-view/PostProductSelect';
import { getDashboardUrl, getNextUrl } from '@funnels/utils';

import {
  ACCOUNT_DETAILS_BASE_NAME,
  ACCOUNTANTS_BASE_NAME,
  APPLICANT_BASE_NAME,
  LINCENSED_BUILDER_BASE_NAME,
  PROPERTIES_BASE_NAME,
  SOLICITOR_BASE_NAME,
} from './constants';

export interface PostProductSelects {
  applicantSelects: PostProductSelectContract[];
  propertySelects: PostProductSelectContract[];
  accountantSelects: PostProductSelectContract[];
  conveyancerSelects: PostProductSelectContract[];
  licensedBuilderSelects: PostProductSelectContract[];
  assetAccountSelects: PostProductSelectContract[];
  liabilityAccountSelects: PostProductSelectContract[];
  liabilitySelects: PostProductSelectContract[];
}

interface ApplicantWithSelfEmploymentStatus {
  id: string;
  selfEmployed: boolean;
}

export function getNextPPSRoute(
  applicationId: string,
  postProductSelects: PostProductSelects,
  currentEntityType?: ENTITY_TYPE,
  optionalTargetParams?: OptionalTargetParams | undefined,
  applicantsWithSelfEmploymentStatus: ApplicantWithSelfEmploymentStatus[] = []
) {
  const {
    applicantSelects,
    propertySelects,
    accountantSelects,
    conveyancerSelects: conveyorSelects,
    licensedBuilderSelects,
    assetAccountSelects,
    liabilityAccountSelects,
  } = postProductSelects;
  const location = optionalTargetParams?.location;

  let nextEntityType = currentEntityType;

  if (nextEntityType === ENTITY_TYPE.APPLICANT) {
    if (applicantSelects.length) {
      return { url: `${APPLICANT_BASE_NAME}` };
    }
    nextEntityType = ENTITY_TYPE.ACCOUNTANT;
  }

  if (nextEntityType === ENTITY_TYPE.ACCOUNTANT) {
    // Here we find the first applicant who has self-employment
    const firstApplicantId = applicantsWithSelfEmploymentStatus.find((a) => a.selfEmployed)?.id;
    if (accountantSelects.length && firstApplicantId) {
      return { url: `${ACCOUNTANTS_BASE_NAME}${firstApplicantId}` };
    }
    nextEntityType = ENTITY_TYPE.ASSET;
  }

  if (nextEntityType === ENTITY_TYPE.ASSET) {
    if (propertySelects.length) {
      return { url: `${PROPERTIES_BASE_NAME}` };
    }
    nextEntityType = ENTITY_TYPE.LICENSED_BUILDER;
  }

  if (nextEntityType === ENTITY_TYPE.LICENSED_BUILDER) {
    if (licensedBuilderSelects.length) {
      return { url: `${LINCENSED_BUILDER_BASE_NAME}${licensedBuilderSelects[0].entityId}` };
    }
    nextEntityType = ENTITY_TYPE.SOLICITOR;
  }

  if (nextEntityType === ENTITY_TYPE.SOLICITOR) {
    if (conveyorSelects.length) {
      return { url: getNextUrl(location, `${SOLICITOR_BASE_NAME}`) };
    }
    nextEntityType = ENTITY_TYPE.ASSET_ACCOUNT;
  }

  if (nextEntityType === ENTITY_TYPE.ASSET_ACCOUNT) {
    if (assetAccountSelects.length || liabilityAccountSelects.length) {
      return { url: getNextUrl(location, `${ACCOUNT_DETAILS_BASE_NAME}`) };
    }
  }
  return { url: getDashboardUrl(applicationId), hardRedirect: true };
}

export function getApplicantQuestionsConfig(applicantSelects: PostProductSelectContract[], target: TargetReturn) {
  if (!applicantSelects.length) {
    return [];
  }
  const applicantConfig: QuestionConfig = {
    url: `${APPLICANT_BASE_NAME}:${URL_PARAM_APPLICANT_ID}?`,
    component: ApplicantLayout,
    target: (values: any, { applicantIndex = 0, location }, reduxStoreData: any) => {
      if (applicantSelects.length === applicantIndex) {
        return target;
      }

      const nextApplicantId = get(
        { postProductSelect: applicantSelects },
        FIELD_PATHS.POST_PRODUCT_SELECT_ENTITY_ID(applicantIndex + 1)
      );
      if (!nextApplicantId) {
        return target;
      }

      return { url: `${APPLICANT_BASE_NAME}${nextApplicantId}` };
    },
    pageTitle: 'We need more information about the applicant',
  };

  return [applicantConfig];
}

export function getPropertyQuestionsConfig(propertySelects: PostProductSelectContract[], target: TargetReturn) {
  if (!propertySelects.length) {
    return [];
  }

  const propertyConfig: QuestionConfig = {
    component: PropertyLayout,
    url: `${PROPERTIES_BASE_NAME}`,
    target(values: any, { location }) {
      return target;
    },
    pageTitle: 'We need more information about your property',
  };
  return [propertyConfig];
}

export function getAccountantQuestionConfig(
  applicantsWithSelfEmploymentStatus: ApplicantWithSelfEmploymentStatus[],
  accountantSelects: PostProductSelectContract[],
  target: TargetReturn
) {
  if (!accountantSelects.length) {
    return [];
  }

  const accountantConfig: QuestionConfig = {
    component: AccountantLayout,
    url: `${ACCOUNTANTS_BASE_NAME}:${URL_PARAM_APPLICANT_ID}?`,
    target(values: any, { applicantIndex = 0 }) {
      // Here we try to find if there is any additional applicant who has self-employment.
      // If no more found, it continues to next part of the post product select.
      const applicantWithSelfEmployment = applicantsWithSelfEmploymentStatus.find(
        (applicant, index) => index > applicantIndex && applicant.selfEmployed
      );
      if (!applicantWithSelfEmployment) {
        return target;
      } else {
        return { url: `${ACCOUNTANTS_BASE_NAME}${applicantWithSelfEmployment.id}` };
      }
    },
    pageTitle: "Please give us details about the applicant's accountant",
  };
  return [accountantConfig];
}

export function getConveyancerQuestionConfig(conveyancerSelects: PostProductSelectContract[], target: TargetReturn) {
  if (!conveyancerSelects.length) {
    return [];
  }

  const conveyancerConfig: QuestionConfig = {
    component: ConveyancerLayout,
    url: `${SOLICITOR_BASE_NAME}`,
    target(values: any, { location }) {
      return target;
    },
    pageTitle: 'Please give us details about your conveyancer',
  };

  return [conveyancerConfig];
}

export function getLicensedBuilderQuestionsConfig(
  licensedBuilderSelects: PostProductSelectContract[],
  target: TargetReturn
) {
  if (!licensedBuilderSelects.length) {
    return [];
  }
  const licensedBuilderConfig: QuestionConfig = {
    url: `${LINCENSED_BUILDER_BASE_NAME}:${URL_PARAM_ASSET_ID}?`,
    component: LicnesedBuilderLayout,
    target: (values: any, { assetIndex = 0 }) => {
      if (licensedBuilderSelects.length === assetIndex) {
        return target;
      }

      const nextAssetId = get(
        { postProductSelect: licensedBuilderSelects },
        FIELD_PATHS.POST_PRODUCT_SELECT_ENTITY_ID(assetIndex + 1)
      );
      if (!nextAssetId) {
        return target;
      }

      return { url: `${LINCENSED_BUILDER_BASE_NAME}${nextAssetId}` };
    },
    pageTitle: 'Please give us the details of your licensed builder',
  };

  return [licensedBuilderConfig];
}

export function getAccountDetailsQuestionConfig(
  assetAccountSelects: PostProductSelectContract[],
  liabilityAccountSelects: PostProductSelectContract[],
  liabilitySelects: PostProductSelectContract[],
  target: TargetReturn
) {
  if (!assetAccountSelects.length && !liabilityAccountSelects.length && !liabilitySelects.length) {
    return [];
  }

  const accountDetails: QuestionConfig = {
    component: AccountDetails,
    url: `${ACCOUNT_DETAILS_BASE_NAME}`,
    target(values: any, { location }) {
      return target;
    },
    pageTitle: 'Provide your account details',
  };

  return [accountDetails];
}

export const getApplicantSelects = (
  filteredApplicants: StoreApplicant[],
  postProductSelects: PostProductSelectContract[]
): PostProductSelectContract[] =>
  filteredApplicants.reduce<PostProductSelectContract[]>(
    (accumulator: PostProductSelectContract[], applicant: StoreApplicant) => {
      const applicantId = get({ applicants: [applicant] }, FIELD_PATHS.APPLICANT_ID(0));
      const applicantSelect = postProductSelects.find((postProductSelect) => {
        const onlyHasSubstantialBenefit = get(
          { postProductSelect: [postProductSelect] },
          FIELD_PATHS.POST_PRODUCT_SELECT_QUESTIONS(0),
          []
        ).every((q: Question) => q === APPLICANT_QUESTIONS.SUBSTANTIAL_BENEFITS);
        return (
          get({ postProductSelect: [postProductSelect] }, FIELD_PATHS.POST_PRODUCT_SELECT_ENTITY_ID(0)) ===
            applicantId &&
          !onlyHasSubstantialBenefit &&
          get({ postProductSelect: [postProductSelect] }, FIELD_PATHS.POST_PRODUCT_SELECT_ENTITY_TYPE(0)) ===
            ENTITY_TYPE.APPLICANT
        );
      });
      return applicantSelect ? [...accumulator, applicantSelect] : accumulator;
    },
    []
  );
export const getPropertySelects = (postProductSelects: PostProductSelectContract[]): PostProductSelectContract[] =>
  postProductSelects.filter(
    (item: PostProductSelectContract) =>
      get({ postProductSelect: [item] }, FIELD_PATHS.POST_PRODUCT_SELECT_ENTITY_TYPE(0)) === ENTITY_TYPE.ASSET
  );

export const getConveyancerSelects = (postProductSelects: PostProductSelectContract[]): PostProductSelectContract[] =>
  postProductSelects.filter(
    (item: PostProductSelectContract) =>
      get({ postProductSelect: [item] }, FIELD_PATHS.POST_PRODUCT_SELECT_ENTITY_TYPE(0)) === ENTITY_TYPE.SOLICITOR
  );

export const getAccountantSelects = (postProductSelects: PostProductSelectContract[]): PostProductSelectContract[] =>
  postProductSelects.filter(
    (item: PostProductSelectContract) =>
      get({ postProductSelect: [item] }, FIELD_PATHS.POST_PRODUCT_SELECT_ENTITY_TYPE(0)) === ENTITY_TYPE.ACCOUNTANT
  );

export const getAssetAccountSelects = (postProductSelects: PostProductSelectContract[]): PostProductSelectContract[] =>
  postProductSelects.filter(
    (item: PostProductSelectContract) =>
      get({ postProductSelect: [item] }, FIELD_PATHS.POST_PRODUCT_SELECT_ENTITY_TYPE(0)) === ENTITY_TYPE.ASSET_ACCOUNT
  );

export const getLiabilityAccountSelects = (postProductSelects: PostProductSelectContract[]): LiabilityContractsMap => {
  const liabilityContracts: LiabilityContractsMap = {};

  postProductSelects.forEach((entity) => {
    if (![ENTITY_TYPE.LIABILITY_ACCOUNT, ENTITY_TYPE.LIABILITY].find((t) => t === entity.entityType)) return; //early quit
    // LIABILITY_ACCOUNT || LIABILITY
    const liabilityId = entity.entityId;
    if (!liabilityId) return; //early quit
    if (!liabilityContracts[liabilityId]) {
      liabilityContracts[liabilityId] = {}; // initialization
    }
    if (entity.entityType === ENTITY_TYPE.LIABILITY) liabilityContracts[liabilityId].liabilityContract = entity;
    if (entity.entityType === ENTITY_TYPE.LIABILITY_ACCOUNT)
      liabilityContracts[liabilityId].liabilityAccountContract = entity;
  });

  return liabilityContracts;
};
